/**
 * StatusPage - Equipment Status Management
 *
 * Industrial control room aesthetic with glowing colour swatches
 * for status preview and data-dense tables for management.
 */

import { useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Card,
  ColorInput,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useStatuses, useCreateStatus, useUpdateStatus, useDeleteStatus } from '@commander/api-client';
import type { Status } from '@commander/api-client';
import { IconPlus, IconEdit, IconTrash } from '@/components/icons';

/** Colour swatch with glow effect matching the status colour */
function ColourSwatch({ colour, size = 24 }: { colour: string; size?: number }) {
  return (
    <Box
      style={{
        width: size,
        height: size,
        backgroundColor: colour,
        borderRadius: 4,
        border: `2px solid ${colour}`,
        boxShadow: `0 0 12px ${colour}80, inset 0 1px 0 rgba(255,255,255,0.2)`,
        transition: 'box-shadow 150ms ease',
      }}
    />
  );
}

/** Status form for create/edit */
interface StatusFormData {
  name: string;
  colour: string;
  description: string;
}

function StatusForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
}: {
  initialData?: StatusFormData;
  onSubmit: (data: StatusFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<StatusFormData>(
    initialData ?? { name: '', colour: '#24a148', description: '' }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label="Status Name"
          placeholder="e.g., Available, In Repair"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
          required
          styles={{
            input: {
              backgroundColor: '#1f1f1f',
              borderColor: '#2a2a2a',
              color: '#f4f4f4',
              '&:focus': { borderColor: '#0f62fe' },
            },
            label: { color: '#a8a8a8' },
          }}
        />

        <ColorInput
          label="Status Colour"
          value={formData.colour}
          onChange={(value) => setFormData({ ...formData, colour: value })}
          format="hex"
          swatches={['#24a148', '#0f62fe', '#da1e28', '#f1c21b', '#8a3ffc', '#525252']}
          styles={{
            input: {
              backgroundColor: '#1f1f1f',
              borderColor: '#2a2a2a',
              color: '#f4f4f4',
            },
            label: { color: '#a8a8a8' },
          }}
        />

        {/* Live preview */}
        <Box>
          <Text size="sm" c="dimmed" mb="xs">
            Preview
          </Text>
          <Group gap="sm" p="md" style={{ backgroundColor: '#161616', borderRadius: 8 }}>
            <ColourSwatch colour={formData.colour} size={32} />
            <Text fw={600} style={{ color: formData.colour }}>
              {formData.name || 'Status Name'}
            </Text>
          </Group>
        </Box>

        <Textarea
          label="Description"
          placeholder="Optional description of when this status is used"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.currentTarget.value })}
          minRows={2}
          styles={{
            input: {
              backgroundColor: '#1f1f1f',
              borderColor: '#2a2a2a',
              color: '#f4f4f4',
            },
            label: { color: '#a8a8a8' },
          }}
        />

        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {initialData ? 'Update Status' : 'Create Status'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export function StatusPage() {
  const { data, isLoading } = useStatuses();
  const createStatus = useCreateStatus();
  const updateStatus = useUpdateStatus();
  const deleteStatus = useDeleteStatus();

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [deletingStatus, setDeletingStatus] = useState<Status | null>(null);

  const handleCreate = async (formData: StatusFormData) => {
    try {
      await createStatus.mutateAsync(formData);
      notifications.show({
        title: 'Status Created',
        message: `"${formData.name}" has been created successfully.`,
        color: 'green',
      });
      closeCreate();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to create status. Please try again.',
        color: 'red',
      });
    }
  };

  const handleUpdate = async (formData: StatusFormData) => {
    if (!editingStatus) return;

    try {
      await updateStatus.mutateAsync({ id: editingStatus.id, ...formData });
      notifications.show({
        title: 'Status Updated',
        message: `"${formData.name}" has been updated successfully.`,
        color: 'green',
      });
      setEditingStatus(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update status. Please try again.',
        color: 'red',
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingStatus) return;

    try {
      await deleteStatus.mutateAsync(deletingStatus.id);
      notifications.show({
        title: 'Status Deleted',
        message: `"${deletingStatus.name}" has been deleted.`,
        color: 'green',
      });
      setDeletingStatus(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Cannot delete status with assigned equipment.',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Status Management</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          Add Status
        </Button>
      </Group>

      <Card withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 50 }}>Colour</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th style={{ width: 120 }}>Equipment</Table.Th>
              <Table.Th style={{ width: 100 }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center">
                    Loading...
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : data?.items.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center">
                    No status values defined. Create one to get started.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              data?.items.map((status) => (
                <Table.Tr key={status.id}>
                  <Table.Td>
                    <ColourSwatch colour={status.colour} />
                  </Table.Td>
                  <Table.Td>
                    <Text fw={600} style={{ color: status.colour }}>
                      {status.name}
                    </Text>
                    {status.isDefault && (
                      <Text size="xs" c="dimmed">
                        Default
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={2}>
                      {status.description || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text
                      ff="monospace"
                      fw={600}
                      style={{
                        color: status.equipmentCount > 0 ? '#f4f4f4' : '#525252',
                      }}
                    >
                      {status.equipmentCount}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label="Edit">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => setEditingStatus(status)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={status.equipmentCount > 0 ? 'Cannot delete - has equipment' : 'Delete'}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeletingStatus(status)}
                          disabled={status.equipmentCount > 0 || !!status.isDefault}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Create Modal */}
      <Modal
        opened={createOpened}
        onClose={closeCreate}
        title="Create Status"
        styles={{
          header: { backgroundColor: '#1f1f1f', borderBottom: '1px solid #2a2a2a' },
          body: { backgroundColor: '#1f1f1f' },
          title: { color: '#f4f4f4', fontWeight: 600 },
        }}
      >
        <StatusForm
          onSubmit={handleCreate}
          onCancel={closeCreate}
          isLoading={createStatus.isPending}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        opened={!!editingStatus}
        onClose={() => setEditingStatus(null)}
        title="Edit Status"
        styles={{
          header: { backgroundColor: '#1f1f1f', borderBottom: '1px solid #2a2a2a' },
          body: { backgroundColor: '#1f1f1f' },
          title: { color: '#f4f4f4', fontWeight: 600 },
        }}
      >
        {editingStatus && (
          <StatusForm
            initialData={{
              name: editingStatus.name,
              colour: editingStatus.colour,
              description: editingStatus.description ?? '',
            }}
            onSubmit={handleUpdate}
            onCancel={() => setEditingStatus(null)}
            isLoading={updateStatus.isPending}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!deletingStatus}
        onClose={() => setDeletingStatus(null)}
        title="Delete Status"
        styles={{
          header: { backgroundColor: '#1f1f1f', borderBottom: '1px solid #2a2a2a' },
          body: { backgroundColor: '#1f1f1f' },
          title: { color: '#da1e28', fontWeight: 600 },
        }}
      >
        <Stack gap="md">
          <Text c="dimmed">
            Are you sure you want to delete the status{' '}
            <Text span fw={600} {...(deletingStatus?.colour ? { c: deletingStatus.colour } : {})}>
              "{deletingStatus?.name}"
            </Text>
            ? This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setDeletingStatus(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteStatus.isPending}>
              Delete Status
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
